import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LandingPage from './components/LandingPage';
import PaymentPage from './components/PaymentPage';
import AdminPage from './components/AdminPage';
import './App.css';

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Application Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Logo component - exact same as PaymentPage
      const Logo = () => (
        <div className="logo">
          <img 
            src="http://cdn.mcauto-images-production.sendgrid.net/a1fe588379782815/8aaa4a53-ccba-431e-b648-fde0e2fae740/4501x834.png" 
            alt="Mint House"
            style={{ 
              height: '65px', 
              width: 'auto',
              objectFit: 'contain',
              mixBlendMode: 'multiply'
            }}
          />
        </div>
      );

      return (
        <div className="error-fallback">
          <div className="error-container">
            <div className="error-content">
              <div className="header">
                <Logo />
              </div>
              <div className="content">
                <div className="error-icon">⚠️</div>
                <h2>Something went wrong</h2>
                <p>We're sorry, but something unexpected happened. Please try refreshing the page.</p>
                <button 
                  onClick={() => window.location.reload()}
                  className="retry-button"
                >
                  Refresh Page
                </button>
                <div className="contact-support">
                  <p>If the problem persists, contact support:</p>
                  <a href="tel:+18559729090">📞 1-855-972-9090</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Loading Component
const AppLoading: React.FC = () => {
  // Logo component - exact same as PaymentPage
  const Logo = () => (
    <div className="logo">
      <img 
        src="http://cdn.mcauto-images-production.sendgrid.net/a1fe588379782815/8aaa4a53-ccba-431e-b648-fde0e2fae740/4501x834.png" 
        alt="Mint House"
        style={{ 
          height: '65px', 
          width: 'auto',
          objectFit: 'contain',
          mixBlendMode: 'multiply'
        }}
      />
    </div>
  );

  return (
    <div className="app-loading">
      <div className="loading-container">
        <div className="header">
          <Logo />
        </div>
        <div className="content">
          <div className="loading-spinner"></div>
          <p>Loading Mint House Portal...</p>
        </div>
      </div>
    </div>
  );
};

// Admin Route Protection
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);

  useEffect(() => {
    // Simple admin access check - in production, use proper authentication
    const checkAdminAccess = () => {
      const adminKey = localStorage.getItem('mint_house_admin_key');
      const urlParams = new URLSearchParams(window.location.search);
      const accessKey = urlParams.get('key');
      
      // Check for admin key in URL or localStorage
      if (accessKey === 'mint2024admin' || adminKey === 'mint2024admin') {
        localStorage.setItem('mint_house_admin_key', 'mint2024admin');
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
      }
    };

    checkAdminAccess();
  }, []);

  if (isAuthorized === null) {
    return <AppLoading />;
  }

  if (!isAuthorized) {
    // Logo component - exact same as PaymentPage
    const Logo = () => (
      <div className="logo">
        <img 
          src="http://cdn.mcauto-images-production.sendgrid.net/a1fe588379782815/8aaa4a53-ccba-431e-b648-fde0e2fae740/4501x834.png" 
          alt="Mint House"
          style={{ 
            height: '65px', 
            width: 'auto',
            objectFit: 'contain',
            mixBlendMode: 'multiply'
          }}
        />
      </div>
    );

    return (
      <div className="access-denied">
        <div className="access-denied-container">
          <div className="access-denied-content">
            <div className="header">
              <Logo />
            </div>
            <div className="content">
              <div className="access-icon">🔒</div>
              <h2>Access Restricted</h2>
              <p>This area is for authorized personnel only.</p>
              <div className="access-actions">
                <a href="/" className="back-link">
                  ← Return to Guest Portal
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

// Main App Component
const AppContent: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate app initialization
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <AppLoading />;
  }

  return (
    <div className="app-wrapper">
      <main className="app-main">
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/pay/:linkId" element={<PaymentPage />} />
          <Route 
            path="/admin" 
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            } 
          />
          {/* Catch all route */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// Main App with Router
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <div className="App">
          <AppContent />
        </div>
      </Router>
    </ErrorBoundary>
  );
}

export default App;